/**
 * GeoTemplate.tsx — IP Geolocation.
 * Covers: ip_geo (geolocation and geolocation_proxy sub-types).
 *
 * Uses the existing `prepareIpGeolocationResult` / `...Proxy` helpers, which
 * already unwrap the awkward payload where every field is nested under a key
 * NAMED AFTER THE IP ADDRESS itself (`data[0]["31.154.214.34"]`). See
 * INTEGRATION.md §Backend notes — that is worth changing at source.
 *
 * `mapSlot` is still accepted (an already-composed override some caller might
 * pass in), but the default is now a real, self-contained map — same
 * schematic/live toggle as SceneTemplate's SceneLocationMap and
 * PersonTemplate's MapCard, not just a static "lat, lon" placeholder.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { prepareIpGeolocationResult, prepareIpGeolocationResultProxy } from '../vendor/searcherService'
import CommonMap from '../vendor/CommonMap'
import {
  Cols, Duo, FieldTable, NoData, RawCard, ResCard, Stack, Stats, ValueList, useTokens,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'
import { radii } from '../tokens'

type Geo = any

/**
 * Free, instant placeholder (pin + coordinates) by default; the real
 * CommonMap (Google Maps) only mounts once the user asks for it, so a
 * missing/unconfigured API key never blocks the default view.
 */
function GeoLocationMap({ point }: { point: { lat: number; lng: number; title: string } | null }) {
  const tk = useTokens()
  const [live, setLive] = React.useState(false)

  if (!point) {
    return (
      <Box sx={{ height: 200, display: 'grid', placeItems: 'center', color: tk.text3, fontSize: 12 }}>
        No coordinates returned
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 200 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setLive((v) => !v)}
        sx={{
          position: 'absolute', top: 6, right: 6, zIndex: 2, fontSize: 9.5, fontWeight: 700, cursor: 'pointer',
          borderRadius: '7px', padding: '3px 8px', border: `1px solid ${tk.border}`, background: tk.surface2,
          color: tk.text2, fontFamily: 'inherit',
          '&:hover': { borderColor: tk.borderStrong, color: tk.text },
        }}
      >
        {live ? '← Schematic' : 'Show live map'}
      </Box>
      {live ? (
        <CommonMap locations={[point]} />
      ) : (
        <Box sx={{ height: '100%', minHeight: 200, background: tk.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', color: '#EF4444' }}>
          <svg width={26} height={26} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 22s7-7.3 7-12a7 7 0 1 0-14 0c0 4.7 7 12 7 12z" />
            <circle cx={12} cy={10} r={2.6} fill={tk.bg} />
          </svg>
          <Typography sx={{ fontSize: 10.5, color: `${tk.text3} !important` }}>
            {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export function GeoTemplate({ apiData, mapSlot }: TemplateProps & { mapSlot?: React.ReactNode }) {
  const tk = useTokens()

  const item: Geo | null = React.useMemo(() => {
    const pick = (fn: (r: any) => any) => {
      try {
        const r = fn(apiData as any)
        return Array.isArray(r?.data) && r.data.length ? r.data[0] : null
      } catch {
        return null
      }
    }
    return pick(prepareIpGeolocationResult) || pick(prepareIpGeolocationResultProxy)
  }, [apiData])

  // Kept ahead of the `if (!item)` bailout below — every Hook in this
  // component has to run on every render regardless of whether `item` ended
  // up null, so geoPoint reads off `item` via optional chaining instead of
  // sitting after the early return.
  const lat = item?.latitude ?? item?.lat
  const lon = item?.longitude ?? item?.lon
  const geoPoint = React.useMemo(() => {
    const latNum = typeof lat === 'string' ? parseFloat(lat) : lat
    const lngNum = typeof lon === 'string' ? parseFloat(lon) : lon
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null
    return { lat: latNum, lng: lngNum, title: [item?.city, item?.country].filter(Boolean).join(', ') || 'Estimated location' }
  }, [lat, lon, item?.city, item?.country])

  if (!item) return <NoData title="No geolocation returned" hint="The provider answered but the address could not be resolved." />

  const proxy = String(item.proxy ?? '').toLowerCase()
  const isProxy = proxy === 'yes' || proxy === 'true' || item.proxy === true
  const currency = item.currency || {}
  const devices = item.devices || {}

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Country', value: item.country ?? '—', sub: [item.isocode, item.continent].filter(Boolean).join(' · ') },
            { label: 'City', value: item.city ?? '—', sub: item.region },
            { label: 'Provider', value: item.provider ?? '—', sub: item.organisation },
            { label: 'ASN', value: item.asn ?? '—', sub: item.range },
            { label: 'Proxy / VPN', value: isProxy ? 'Yes' : 'No', sub: isProxy ? 'anonymised' : 'clean', tone: isProxy ? 'bad' : 'ok' },
            { label: 'Type', value: item.type ?? '—', tone: 'hl' },
          ]}
        />
      </ResCard>

      <Cols n={2}>
        <Stack>
          <ResCard title="Location" fill>
            <Duo gap={14}>
              <Box sx={{ minHeight: 200, borderRadius: `${radii.md}px`, overflow: 'hidden', border: `1px solid ${tk.border}`, position: 'relative', background: tk.surface3 }}>
                {mapSlot ?? <GeoLocationMap point={geoPoint} />}
              </Box>
              <FieldTable
                rows={[
                  ['Continent', `${item.continent ?? '—'}${item.continentcode ? ` (${item.continentcode})` : ''}`],
                  ['Country', `${item.country ?? '—'}${item.isocode ? ` (${item.isocode})` : ''}`],
                  ['Region', `${item.region ?? '—'}${item.regioncode ? ` (${item.regioncode})` : ''}`],
                  ['City', item.city ?? '—'],
                  ['Timezone', item.timezone ?? '—'],
                  ['Coordinates', lat != null && lon != null ? `${lat}, ${lon}` : '—'],
                ]}
              />
            </Duo>
          </ResCard>
        </Stack>

        <Stack>
          <ResCard title="Network">
            <FieldTable
              rows={[
                ['ASN', item.asn ?? '—'],
                ['Range', item.range ?? '—'],
                ['Provider', item.provider ?? '—'],
                ['Organisation', item.organisation ?? '—'],
                ['Devices on subnet', String(devices.subnet ?? '—')],
                ['Devices on address', String(devices.address ?? '—')],
              ]}
            />
          </ResCard>

          <ResCard title="Risk signals" count={3} tone={isProxy ? 'accent' : 'good'}>
            <ValueList
              rows={[
                {
                  value: isProxy ? 'Proxy or VPN detected' : 'Not a proxy or VPN',
                  sources: ['ip'],
                  flag: { tone: isProxy ? 'bad' : 'ok', label: isProxy ? 'anonymised' : 'clean' },
                },
                { value: `${item.type ?? 'Unknown'} connection`, sources: ['ip'], note: item.type === 'Business' ? 'not a residential line' : undefined },
                { value: `${devices.subnet ?? '—'} devices on the subnet`, sources: ['ip'], note: Number(devices.subnet) <= 4 ? 'small allocation' : undefined },
              ]}
            />
          </ResCard>

          {(currency.code || item.timezone) && (
            <ResCard title="Locale">
              <FieldTable
                rows={[
                  ['Currency', currency.code ? `${currency.name ?? ''} (${currency.code}${currency.symbol ? ` ${currency.symbol}` : ''})` : '—'],
                  ['Local time', item.timezone ?? '—'],
                ]}
              />
            </ResCard>
          )}
        </Stack>
      </Cols>

      <Typography sx={{ fontSize: 11.5, color: tk.text3 }}>
        Coordinates are network-level and typically accurate to the town, not the address.
      </Typography>

      <RawCard data={apiData} />
    </>
  )
}
